import { createSlice } from '@reduxjs/toolkit';
import {
  UnloadingPoint,
  UnloadingPointInfo
} from 'modules/inbound/entriesST12/domain/unloadingPoint';
import { getUnloadingPointInfo } from './async';
import {
  DividerInfo,
  PointsInfoDataItem,
  UnloadingPointsDataBufferType,
  UnloadingPointState
} from './types';

const emptyUnloadingPointInfo = new UnloadingPointInfo();

const dividerOrder: Array<DividerInfo['title']> = [
  'A',
  'T',
  'K',
  'M',
  'V',
  'VG'
];

const t17DividerOrder: Array<DividerInfo['title']> = ['A T17', 'B T17'];

const getT17Zone = (lastPosition?: string): 'A T17' | 'B T17' | null => {
  const match = lastPosition?.toUpperCase().match(/^T17([AB])_EXT[ _]*\d+$/);

  if (!match) {
    return null;
  }

  return `${match[1]} T17` as 'A T17' | 'B T17';
};

const getT17Extension = (lastPosition: string): number => {
  const match = lastPosition.match(/_EXT[ _]*(\d+)$/i);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
};

const normalizeLastPosition = (lastPosition?: string) =>
  (lastPosition?.slice(3, 5) || '').toUpperCase();

const getDividerTitle = (
  lastPosition?: string
): DividerInfo['title'] | null => {
  const normalized = normalizeLastPosition(lastPosition);

  if (normalized.includes('VG')) {
    return 'VG';
  }

  for (const title of ['A', 'T', 'K', 'M', 'V'] as const) {
    if (normalized.includes(title)) {
      return title;
    }
  }

  return null;
};

const orderT17PointsInfoData = (
  unloadingPoints: UnloadingPoint[]
): PointsInfoDataItem[] => {
  const grouped = t17DividerOrder.reduce<Record<string, UnloadingPoint[]>>(
    (acc, title) => {
      acc[title] = [];
      return acc;
    },
    {}
  );

  unloadingPoints.forEach((item) => {
    const title = getT17Zone(item.lastPosition);

    if (title) {
      grouped[title].push(item);
    }
  });

  return t17DividerOrder.reduce<PointsInfoDataItem[]>((acc, title) => {
    const items = grouped[title];

    if (items.length === 0) {
      return acc;
    }

    acc.push({ type: 'divider', title });
    acc.push(
      ...items.sort(
        (a, b) =>
          getT17Extension(a.lastPosition) - getT17Extension(b.lastPosition)
      )
    );

    return acc;
  }, []);
};

const orderPointsInfoData = (
  unloadingPoints: UnloadingPoint[]
): PointsInfoDataItem[] => {
  const grouped = dividerOrder.reduce<Record<string, UnloadingPoint[]>>(
    (acc, title) => {
      acc[title] = [];
      return acc;
    },
    {}
  );
  unloadingPoints.forEach((item) => {
    const title = getDividerTitle(item.lastPosition);

    if (title) {
      grouped[title].push(item);
    }
  });

  return dividerOrder.reduce<PointsInfoDataItem[]>((acc, title) => {
    const items = grouped[title];

    if (items.length === 0) {
      return acc;
    }

    acc.push({ type: 'divider', title });
    acc.push(
      ...items.sort((a, b) => {
        const aPos = normalizeLastPosition(a.lastPosition);
        const bPos = normalizeLastPosition(b.lastPosition);

        return aPos.localeCompare(bPos, undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      })
    );

    return acc;
  }, []);
};

const orderBufferInfoData = (
  bufferUnloadingPoints: UnloadingPoint[],
  unloadingPointInfoData: UnloadingPointInfo
): UnloadingPointsDataBufferType => {
  return {
    timestamp: unloadingPointInfoData.timestamp,
    workshop: unloadingPointInfoData.workshop,
    bufferList: bufferUnloadingPoints.map((item) => ({
      type: 'divider' as const,
      title: getDividerTitle(item.lastPosition) ?? 'A',
      unloadingPoints: [item]
    })) as UnloadingPointsDataBufferType['bufferList'],
    totalTrucks: unloadingPointInfoData.totalTrucks
  };
};

export const initialState: UnloadingPointState = {
  unloadingPointInfoData: emptyUnloadingPointInfo,
  unloadingPointInfoRequestStatus: '',
  ParkingsTCCInfoData: [],
  BufferInfoData: {
    timestamp: '',
    workshop: '',
    bufferList: [],
    totalTrucks: 0
  },
  PointsInfoData: [],
  T17PointsInfoData: []
};

const unloadingPointSlice = createSlice({
  name: 'unloadingPointSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getUnloadingPointInfo.fulfilled, (state, action) => {
      state.unloadingPointInfoData = action.payload || emptyUnloadingPointInfo;
      const unloadingPoints =
        state.unloadingPointInfoData.unloadingPoints || [];

      state.ParkingsTCCInfoData = unloadingPoints.filter(
        (item) => item?.lastPosition === 'PARKING TCC'
      );
      const bufferUnloadingPointsFilter = unloadingPoints.filter((item) =>
        (item?.lastPosition || '').includes('BUFFER')
      );

      state.BufferInfoData = orderBufferInfoData(
        bufferUnloadingPointsFilter,
        state.unloadingPointInfoData
      );

      const unloadingPointItems = unloadingPoints.filter(
        (item) =>
          item?.lastPosition !== 'PARKING TCC' &&
          !(item?.lastPosition || '').includes('BUFFER')
      );

      state.PointsInfoData = orderPointsInfoData(
        unloadingPointItems.filter((item) => !getT17Zone(item.lastPosition))
      );
      state.T17PointsInfoData = orderT17PointsInfoData(unloadingPointItems);

      state.unloadingPointInfoRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getUnloadingPointInfo.pending, (state, action) => {
      state.unloadingPointInfoRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getUnloadingPointInfo.rejected, (state, action) => {
      state.unloadingPointInfoRequestStatus = action.meta.requestStatus;
    });
  }
});

export const getUnloadingPointInfoData = (state) =>
  state.unloadingPointSliceState.unloadingPointInfoData;
export const getParkingsTCCInfoData = (state) =>
  state.unloadingPointSliceState.ParkingsTCCInfoData;
export const getBufferInfoData = (state) =>
  state.unloadingPointSliceState.BufferInfoData;
export const getPointsInfoData = (state) =>
  state.unloadingPointSliceState.PointsInfoData;
export const getT17PointsInfoData = (state) =>
  state.unloadingPointSliceState.T17PointsInfoData;

export const getUnloadingPointInfoRequestStatusIsPending = (state) =>
  state.unloadingPointSliceState.unloadingPointInfoRequestStatus === 'pending';
export const getUnloadingPointInfoRequestStatusHasError = (state) =>
  state.unloadingPointSliceState.unloadingPointInfoRequestStatus === 'rejected';
export const getUnloadingPointInfoRequestStatusIsSucessfull = (state) =>
  state.unloadingPointSliceState.unloadingPointInfoRequestStatus ===
  'fulfilled';

export default unloadingPointSlice.reducer;
